import type { EvaluationCounts, EvaluationCountsPort } from '../consumer/workbench';
import { parseForecastFailure } from './forecasting-http';
import { createMonitoringRequest, MonitoringReadError } from './http';

export function parseEvaluationCounts(value: unknown): EvaluationCounts {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new MonitoringReadError('WIRE');
  const data = value as Record<string, unknown>;
  if (
    Object.keys(data).sort().join(',') !== 'areaId,evaluated,generatedAt,pending,unavailable' ||
    !(
      data.areaId === null ||
      (typeof data.areaId === 'string' && /^[a-z][a-z0-9-]{0,63}$/.test(data.areaId))
    ) ||
    typeof data.generatedAt !== 'string' ||
    !/(?:Z|[+-]\d{2}:\d{2})$/.test(data.generatedAt) ||
    !Number.isFinite(Date.parse(data.generatedAt)) ||
    ['pending', 'evaluated', 'unavailable'].some(
      (key) => !Number.isSafeInteger(data[key]) || Number(data[key]) < 0,
    )
  )
    throw new MonitoringReadError('WIRE');
  return value as EvaluationCounts;
}

export function createEvaluationCountsAdapter(options: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): EvaluationCountsPort {
  const request = createMonitoringRequest(options);
  return {
    read: async (areaId, signal) => {
      if (areaId && !/^[a-z][a-z0-9-]{0,63}$/.test(areaId))
        throw new Error('Invalid evaluation scope');
      const result = await request(
        `/read-models/normal-monitoring/forecasting/evaluation-counts${areaId ? `?areaId=${encodeURIComponent(areaId)}` : ''}`,
        parseEvaluationCounts,
        signal,
        undefined,
        parseForecastFailure,
      );
      if (result.areaId !== (areaId || null)) throw new MonitoringReadError('WIRE');
      return result;
    },
  };
}
